import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, timer } from 'rxjs';
import { AuthService } from '../services/auth.service';

const ROOT_REDIRECT_TIMEOUT_MS = 1000;

/**
 * Root Redirect Guard
 *
 * Handles the root route (`/`) by redirecting authenticated users to the dashboard
 * and unauthenticated users to the login page.
 */
export const rootRedirectGuard: CanActivateFn = (route, state): Observable<boolean | UrlTree> => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return new Observable<boolean | UrlTree>((observer) => {
    // Check if already authenticated (synchronous check)
    if (authService.isAuthenticated) {
      observer.next(router.parseUrl('/dashboard'));
      observer.complete();
      return;
    }

    // Wait for auth state to be determined
    let redirected = false;

    const subscription = authService.isAuthenticated$.subscribe((isAuth) => {
      if (!redirected) {
        redirected = true;
        if (isAuth) {
          observer.next(router.parseUrl('/dashboard'));
        } else {
          observer.next(router.parseUrl('/login'));
        }
        observer.complete();
      }
    });

    // After timeout, if no auth state determined, redirect to login
    timer(ROOT_REDIRECT_TIMEOUT_MS).subscribe(() => {
      if (!redirected) {
        redirected = true;
        subscription.unsubscribe();
        observer.next(router.parseUrl('/login'));
        observer.complete();
      }
    });

    return () => subscription.unsubscribe();
  });
};